import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projects as projectsApi } from '@/api/projects';
import { organizations as orgApi } from '@/api/organizations';
import { useRequireOrg } from '@/tenancy/OrgContext';
import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import EmptyState from '@/components/EmptyState';
import BranchListEditor from '@/components/BranchListEditor';
import Pagination from '@/components/Pagination';
import { useTablePageSize } from '@/lib/useTablePageSize';
import { formatDateTime } from '@/lib/format';
import type { ProjectBranchPolicy, ProjectView } from '@/api/types';

/** Render branch patterns for display, showing the `*` wildcard as a readable label. */
function formatBranches(branches: string[]): string {
  if (branches.length === 0) return 'none';
  return branches.map(b => (b === '*' ? 'all branches' : b)).join(', ');
}

export default function ProjectsPage() {
  const { id: orgId, org } = useRequireOrg();
  const canEditSettings = org.role === 'OWNER' || org.role === 'ADMIN';
  const [pageSize, setPageSize] = useTablePageSize('projects', 20);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('');
  const [editingProject, setEditingProject] = useState<ProjectView | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['projects', orgId],
    queryFn: () => projectsApi.list(orgId),
  });

  const { data: orgData } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => orgApi.get(orgId),
  });
  const orgDefault = orgData?.defaultAllowedBranches ?? [];

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      p =>
        p.fullName.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.owner.toLowerCase().includes(q),
    );
  }, [data, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const visible = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle={data ? `${data.length} repositor${data.length === 1 ? 'y' : 'ies'}` : undefined}
      />

      <section className="filters card">
        <div className="filter-row">
          <label style={{ flex: 1 }}>
            <span className="muted small">Filter</span>
            <input
              className="input"
              placeholder="Filter by name or owner…"
              value={filter}
              onChange={e => {
                setFilter(e.target.value);
                setPage(0);
              }}
            />
          </label>
        </div>
      </section>

      {isLoading ? <Spinner label="Loading projects…" /> : null}
      {error ? <ErrorBanner error={error} onRetry={() => refetch()} /> : null}

      {data && data.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="Repositories appear automatically once a pull request is analyzed."
        />
      ) : null}

      {data && data.length > 0 ? (
        <>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Owner</th>
                  <th>Full name</th>
                  <th>Branch policy</th>
                  <th>Added</th>
                  {canEditSettings ? <th aria-label="Actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {visible.map(p => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td className="muted">{p.owner}</td>
                    <td className="muted">{p.fullName}</td>
                    <td>
                      <BranchPolicySummary project={p} orgDefault={orgDefault} />
                    </td>
                    <td className="muted small">{formatDateTime(p.createdAt)}</td>
                    {canEditSettings ? (
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          aria-label={`Settings for ${p.fullName}`}
                          title="Project settings"
                          onClick={() => setEditingProject(p)}
                        >
                          ⚙
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={safePage}
            totalPages={totalPages}
            onPrev={() => setPage(p => Math.max(0, p - 1))}
            onNext={() => setPage(p => p + 1)}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
          />
        </>
      ) : null}

      {editingProject ? (
        <ProjectSettingsModal
          project={editingProject}
          orgId={orgId}
          onClose={() => setEditingProject(null)}
        />
      ) : null}
    </>
  );
}

function BranchPolicySummary({ project, orgDefault }: { project: ProjectView; orgDefault: string[] }) {
  if (project.analysisBranchPolicy === 'COMPANY_DEFAULT') {
    return (
      <span className="muted small">
        Company default <span className="muted">({formatBranches(orgDefault)})</span>
      </span>
    );
  }
  const branches = project.analysisAllowedBranches;
  if (branches.length === 0) {
    return <span className="muted small">Custom (empty — nothing will analyze)</span>;
  }
  return <span className="small">Custom: {formatBranches(branches)}</span>;
}

interface ModalProps {
  project: ProjectView;
  orgId: string;
  onClose: () => void;
}

function ProjectSettingsModal({ project, orgId, onClose }: ModalProps) {
  const qc = useQueryClient();
  const [policy, setPolicy] = useState<ProjectBranchPolicy>(project.analysisBranchPolicy);
  const [branches, setBranches] = useState<string[]>(project.analysisAllowedBranches);

  const { data: org } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => orgApi.get(orgId),
  });
  const [orgBranches, setOrgBranches] = useState<string[] | null>(null);
  // Seed the editable company-default list from the org once it loads.
  useEffect(() => {
    if (org && orgBranches === null) setOrgBranches(org.defaultAllowedBranches);
  }, [org, orgBranches]);

  const { data: observedBranches } = useQuery({
    queryKey: ['project-branches', orgId, project.id],
    queryFn: () => projectsApi.branches(orgId, project.id),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      // If the company default was edited, persist it first (applies to every project on it).
      if (org && orgBranches && !sameList(orgBranches, org.defaultAllowedBranches)) {
        await orgApi.updateDefaultBranches(orgId, orgBranches);
      }
      await projectsApi.update(orgId, project.id, {
        analysisBranchPolicy: policy,
        analysisAllowedBranches: branches,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', orgId] });
      qc.invalidateQueries({ queryKey: ['organization', orgId] });
      onClose();
    },
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Settings — {project.fullName}</h3>
        <p className="muted small" style={{ marginTop: 0 }}>
          Choose which pull requests trigger an AI analysis. Excluded PRs are silently skipped —
          no credit is charged.
        </p>

        <div className="policy-options">
          <label className={`policy-option${policy === 'COMPANY_DEFAULT' ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name="policy"
              checked={policy === 'COMPANY_DEFAULT'}
              onChange={() => setPolicy('COMPANY_DEFAULT')}
            />
            <span>
              <strong>Use company default</strong>
              <span className="muted small">
                One shared branch list for every project on this setting. Edit it below and it
                updates everywhere.
              </span>
            </span>
          </label>
          <label className={`policy-option${policy === 'ALLOWLIST' ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name="policy"
              checked={policy === 'ALLOWLIST'}
              onChange={() => setPolicy('ALLOWLIST')}
            />
            <span>
              <strong>Custom branches for this project</strong>
              <span className="muted small">
                Override the company default with a list just for {project.name}.
              </span>
            </span>
          </label>
        </div>

        {policy === 'COMPANY_DEFAULT' ? (
          <div style={{ marginTop: '0.85rem' }}>
            <span className="muted small">Company default branches</span>
            {orgBranches === null ? (
              <Spinner label="Loading company default…" />
            ) : (
              <BranchListEditor value={orgBranches} onChange={setOrgBranches} suggestions={observedBranches} />
            )}
            <p className="muted small" style={{ marginTop: '0.4rem' }}>
              ⚠ Editing this affects every project set to the company default.
            </p>
          </div>
        ) : (
          <div style={{ marginTop: '0.85rem' }}>
            <span className="muted small">Allowed branches for {project.name}</span>
            <BranchListEditor value={branches} onChange={setBranches} suggestions={observedBranches} />
          </div>
        )}

        {saveMutation.error ? (
          <div style={{ marginTop: '0.75rem' }}>
            <ErrorBanner error={saveMutation.error} />
          </div>
        ) : null}

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Order-insensitive equality for branch lists. */
function sameList(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}
